import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { useStyles } from './Styles';
import PoIssuesTable from './PoIssuesTable';
import ProcessFlowConfigDialog from './ProcessFlowConfigDialog';
import ProductSearchSelect from './ProductSearchSelect';
import { ISSUE_TYPES } from './poIssuesData';
import {
  fetchAllPOAlerts,
  fetchCMOOptions,
  fetchPOStatusOptions,
  markPOAlertAsResolved,
  toStamp,
} from './poAlertsAPI';

/**
 * PO Issues Dashboard (BCM-897).
 *
 * Deck behaviours implemented here:
 *  • Open / Closed tabs with live counts in the label.
 *  • Issue / CMO / PO Status are MULTI-select dropdowns; Product is a
 *    type-to-search dropdown (ProductSearchSelect), matching the Batch
 *    Execution dashboard's product filter.
 *  • Sorted by latest issue on top; no pagination — the table body scrolls.
 *  • "Mark as Resolved" moves a row from Open to Closed, stamping the current
 *    user + timestamp as "Resolved by" (Delivery Mismatch rows only).
 *
 * Rows come from /fetchAllPOAlerts (see poAlertsAPI.js), which applies the
 * Issue / Product / CMO / PO Status filters server-side — changing any of them
 * refetches.
 *
 * Dropdown options:
 *   • CMO       → /fetchCMOMembersDirect (the network's CMOs)
 *   • PO Status → /fetchAllPOStatuses (the status master)
 *   • Issue     → the fixed five-value enum the API validates against
 * Both master lists are unioned with the values present in the alerts, so an
 * option never goes missing if a master call fails or returns a short list.
 */

const ALL = '__all__';

/*
 * "Cancelled" is not one of the statuses /fetchAllPOStatuses returns — a
 * cancelled PO keeps its own status and carries the schedule line's
 * markedForDelete flag, which is what paints its number red. So it is appended
 * to the PO Status list as an extra option and sent as the separate
 * includeCancelled boolean, which the API ORs with statusList. Nothing is
 * filtered here: picking Cancelled alone returns only cancelled POs, and
 * picking it alongside statuses returns both sets.
 */
const CANCELLED_OPTION = 'Cancelled';

// Distinct, sorted, blank-free option list for a derived filter dropdown.
const optionsFrom = (rows, key) =>
  [...new Set(rows.map((r) => String(r?.[key] || '').trim()).filter(Boolean))].sort();

// Newest first (deck: "Table is sorted by latest issue on top").
const byNewest = (a, b) => {
  const key = (r) => `${r.timestamp?.date || ''} ${r.timestamp?.time || ''}`;
  return new Date(key(b)) - new Date(key(a));
};

const PoIssuesDashboard = () => {
  const classes = useStyles();

  const [tab, setTab] = useState(0);
  const [openRows, setOpenRows] = useState([]);
  const [closedRows, setClosedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Multi-selects hold an array; [] means "All".
  const [issueFilter, setIssueFilter] = useState([]);
  const [cmoFilter, setCmoFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  // The selected option, not a string — the field shows "<code> / <name>" while
  // only the code is sent to the API.
  const [product, setProduct] = useState(null);
  // PO Number is applied on Enter/blur rather than per keystroke, so typing a
  // long number does not fire a request per character.
  const [poNumberInput, setPoNumberInput] = useState('');
  const [poNumberQuery, setPoNumberQuery] = useState('');

  const [processFlowOpen, setProcessFlowOpen] = useState(false);
  // Which row is mid-resolve (disables its button), the last resolve failure,
  // and a counter used to force a reload after a 404.
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveError, setResolveError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // Dropdown options and the name/text -> numeric id maps the API filters need,
  // captured from one unfiltered load. Held separately from the row state so a
  // filtered response never shrinks the option lists the user is choosing from.
  const [catalog, setCatalog] = useState({
    cmoOptions: [],
    statusOptions: [],
    vendorIDByCmo: {},
    statusIDByText: {},
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Master lists first, one unfiltered alert load for the ids actually in
      // use. Each is independent: if a master call fails its dropdown falls back
      // to the values present in the alerts.
      const [cmos, statuses, alerts] = await Promise.all([
        fetchCMOOptions().catch(() => []),
        fetchPOStatusOptions().catch(() => []),
        fetchAllPOAlerts().catch(() => null),
      ]);
      if (cancelled) return;

      const rows = alerts ? [...alerts.open, ...alerts.closed] : [];
      const vendorIDByCmo = {};
      const statusIDByText = {};
      rows.forEach((r) => {
        if (r.cmo && r.vendorOrgID != null) vendorIDByCmo[r.cmo] = r.vendorOrgID;
        if (r.poStatus && r.poStatusID != null) statusIDByText[r.poStatus] = r.poStatusID;
      });

      cmos.forEach((c) => {
        if (!(c.name in vendorIDByCmo)) vendorIDByCmo[c.name] = c.id;
      });
      // An id seen in the alert rows wins over the master's: po_alerts_log takes
      // poStatusID from the SAP schedule-line status, which the master may not
      // share, and filtering on a wrong id would silently return nothing.
      statuses.forEach((s) => {
        if (!(s.text in statusIDByText)) statusIDByText[s.text] = s.id;
      });

      const merge = (fromApi, fromRows) =>
        [...new Set([...fromApi, ...fromRows])].sort();

      setCatalog({
        cmoOptions: merge(cmos.map((c) => c.name), optionsFrom(rows, 'cmo')),
        statusOptions: merge(statuses.map((s) => s.text), optionsFrom(rows, 'poStatus')),
        vendorIDByCmo,
        statusIDByText,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { cmoOptions } = catalog;

  // Cancelled is appended rather than stored in the catalog, so a status master
  // that ever starts returning it does not produce a duplicate entry.
  const statusOptions = useMemo(
    () =>
      catalog.statusOptions.includes(CANCELLED_OPTION)
        ? catalog.statusOptions
        : [...catalog.statusOptions, CANCELLED_OPTION],
    [catalog.statusOptions]
  );

  const includeCancelled = statusFilter.includes(CANCELLED_OPTION);

  // Cancelled has no id, so only the real statuses go into statusList; the API
  // ORs the two. Filtering out the undefined here (rather than letting
  // positiveInts drop it) keeps the two halves of the filter explicit.
  const statusIDsForRequest = useMemo(
    () =>
      statusFilter
        .filter((text) => text !== CANCELLED_OPTION)
        .map((text) => catalog.statusIDByText[text]),
    [statusFilter, catalog]
  );

  // Shared by the alert query and the product dropdown, so both agree on which
  // CMOs are in scope.
  const selectedVendorIDs = useMemo(
    () => cmoFilter.map((name) => catalog.vendorIDByCmo[name]).filter((id) => id != null),
    [cmoFilter, catalog]
  );

  // Sent as productSearchString, which the API matches against productCode /
  // productDescription, so the code is the precise value.
  const productQuery = useMemo(() => {
    if (!product) return '';
    const isCMO = localStorage.getItem('collabType') === 'CMO';
    return (
      (isCMO ? product.cmoFGMaterialCode : product.fgMaterialCode) ||
      (isCMO ? product.cmoFGMaterialName : product.fgMaterialName) ||
      ''
    );
  }, [product]);

  // Issue / Product / CMO / PO Status go to the API as the documented filters;
  // alertStatus is not sent, so both tab counts stay live off one response.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const { open, closed } = await fetchAllPOAlerts({
          issues: issueFilter,
          productSearch: productQuery,
          poNumberSearch: poNumberQuery,
          vendorIDs: selectedVendorIDs,
          statusIDs: statusIDsForRequest,
          includeCancelled,
        });
        if (cancelled) return;
        setOpenRows(open);
        setClosedRows(closed);
      } catch (error) {
        if (cancelled) return;
        setOpenRows([]);
        setClosedRows([]);
        setLoadError(error?.message || 'Unable to fetch PO alerts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    issueFilter,
    selectedVendorIDs,
    statusIDsForRequest,
    includeCancelled,
    productQuery,
    poNumberQuery,
    catalog,
    reloadKey,
  ]);

  // Shared multi-select onChange: the sentinel "All" item clears the selection.
  const handleMultiChange = (setter, options) => (e) => {
    const val = e.target.value;
    if (val[val.length - 1] === ALL) {
      setter(val.length - 1 === options.length ? [] : []);
      return;
    }
    setter(val.filter((v) => v !== ALL));
  };

  const renderMulti = (selected, options) => {
    if (!selected || selected.length === 0 || selected.length === options.length) {
      return 'All';
    }
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  };

  // Issue / Product / CMO / PO Status are applied by the API, so the rows arrive
  // already filtered — only the Open/Closed split (alertStatus is not sent) and
  // the sort happen here. Re-sorting matters after a local Mark as Resolved.
  const filteredOpen = useMemo(() => [...openRows].sort(byNewest), [openRows]);
  const filteredClosed = useMemo(() => [...closedRows].sort(byNewest), [closedRows]);

  const clearAllFilters = () => {
    setIssueFilter([]);
    setCmoFilter([]);
    setStatusFilter([]);
    setProduct(null);
    setPoNumberInput('');
    setPoNumberQuery('');
  };

  /**
   * Resolving persists via PATCH /markPOAlertAsResolved, then moves the row
   * Open → Closed stamped with what the SERVER recorded — the previous version
   * guessed the name from localStorage and the time from the browser clock, so
   * "Resolved by" could disagree with the database.
   *
   * A 404 means the alert is no longer open (someone else resolved it first),
   * which the API doc says to treat as "refresh the list" rather than an error.
   */
  const handleMarkResolved = async (row) => {
    if (resolvingId) return;
    setResolvingId(row.id);
    setResolveError('');
    try {
      const updated = await markPOAlertAsResolved(row);
      const stamp = toStamp(updated?.lastUpdatedDate);
      setOpenRows((prev) => prev.filter((r) => r.id !== row.id));
      setClosedRows((prev) => [
        {
          ...row,
          alertStatus: 'Closed',
          showMarkAsResolved: false,
          resolvedBy: { name: updated?.lastUpdatedBy || '', ...stamp },
        },
        ...prev,
      ]);
    } catch (error) {
      if (error?.status === 404) {
        setReloadKey((k) => k + 1);
        setResolveError(
          'That alert is no longer open — the list has been refreshed.'
        );
      } else {
        setResolveError(error?.message || 'Unable to mark PO alert as resolved.');
      }
    } finally {
      setResolvingId(null);
    }
  };

  const currentRows = tab === 0 ? filteredOpen : filteredClosed;

  return (
    <div className={classes.root}>
      {/* ---- Open / Closed tabs ---- */}
      <div className={classes.tabsBar}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
          textColor="inherit"
        >
          <Tab
            className={classes.tab}
            label={
              <>
                Open<span className={classes.tabCount}>({filteredOpen.length})</span>
              </>
            }
            disableRipple
          />
          <Tab
            className={classes.tab}
            label={
              <>
                Closed<span className={classes.tabCount}>({filteredClosed.length})</span>
              </>
            }
            disableRipple
          />
        </Tabs>
      </div>

      {/* ---- filters ---- */}
      <div className={classes.filterBar}>
        <div className={classes.filterField}>
          <Typography className={classes.filterLabel}>Alert:</Typography>
          <FormControl>
            <Select
              multiple
              displayEmpty
              variant="outlined"
              className={classes.select}
              value={issueFilter}
              onChange={handleMultiChange(setIssueFilter, ISSUE_TYPES)}
              renderValue={() => renderMulti(issueFilter, ISSUE_TYPES)}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, getContentAnchorEl: null }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={issueFilter.length === 0} />
                All
              </MenuItem>
              {ISSUE_TYPES.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox size="small" checked={issueFilter.includes(opt)} />
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={classes.filterField}>
          <Typography className={classes.filterLabel}>CMO:</Typography>
          <FormControl>
            <Select
              multiple
              displayEmpty
              variant="outlined"
              className={classes.select}
              value={cmoFilter}
              onChange={handleMultiChange(setCmoFilter, cmoOptions)}
              renderValue={() => renderMulti(cmoFilter, cmoOptions)}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, getContentAnchorEl: null }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={cmoFilter.length === 0} />
                All
              </MenuItem>
              {cmoOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox size="small" checked={cmoFilter.includes(opt)} />
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={classes.filterField}>
          <Typography className={classes.filterLabel}>Product:</Typography>
          <ProductSearchSelect
            value={product}
            onChange={setProduct}
            vendorIDs={selectedVendorIDs}
          />
        </div>

        {/* PO Number is free text rather than a dropdown: there is no endpoint
            listing PO numbers, and the API matches it with ILIKE, so a partial
            number finds the PO. Applied on submit, not per keystroke. */}
        <div className={classes.filterField}>
          <Typography className={classes.filterLabel}>PO Number:</Typography>
          <TextField
            variant="outlined"
            placeholder="Search PO number"
            className={classes.poNumberInput}
            value={poNumberInput}
            onChange={(e) => setPoNumberInput(e.target.value)}
            onBlur={() => setPoNumberQuery(poNumberInput)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPoNumberQuery(poNumberInput);
            }}
          />
        </div>

        <div className={classes.filterField}>
          <Typography className={classes.filterLabel}>PO Status:</Typography>
          <FormControl>
            <Select
              multiple
              displayEmpty
              variant="outlined"
              className={classes.select}
              value={statusFilter}
              onChange={handleMultiChange(setStatusFilter, statusOptions)}
              renderValue={() => renderMulti(statusFilter, statusOptions)}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, getContentAnchorEl: null }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={statusFilter.length === 0} />
                All
              </MenuItem>
              {statusOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox size="small" checked={statusFilter.includes(opt)} />
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <span
          className={classes.clearLink}
          onClick={clearAllFilters}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') clearAllFilters();
          }}
        >
          Clear All Filters
        </span>

        <Typography className={classes.countLabel}>
          {currentRows.length} {currentRows.length === 1 ? 'PO' : 'POs'}
        </Typography>
      </div>

      {/* ---- table ---- */}
      {loadError ? (
        <Typography className={classes.loadErrorText}>{loadError}</Typography>
      ) : (
        <>
          {resolveError ? (
            <Typography className={classes.resolveError}>{resolveError}</Typography>
          ) : null}
          <PoIssuesTable
            classes={classes}
            rows={currentRows}
            variant={tab === 0 ? 'open' : 'closed'}
            loading={loading}
            resolvingId={resolvingId}
            onOpenProcessFlow={() => setProcessFlowOpen(true)}
            onMarkResolved={handleMarkResolved}
          />
        </>
      )}

      <ProcessFlowConfigDialog
        open={processFlowOpen}
        onClose={() => setProcessFlowOpen(false)}
        classes={classes}
      />
    </div>
  );
};

export default PoIssuesDashboard;
